import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { validateOTP } from '../../utils/validation';
import LoadingSpinner from '../shared/LoadingSpinner';
import ErrorMessage from '../shared/ErrorMessage';
import CountdownTimer from '../shared/CountdownTimer';

const StepOTPVerification = ({
    otp,
    setOtp,
    otpExpiresAt,
    onSubmit,
    onResendOTP,
    loading,
    error,
}) => {
    const [otpError, setOtpError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(60); // 60 seconds cooldown
    const [canResend, setCanResend] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        // Countdown for resend button
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendCooldown]);

    const handleOtpChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value)) return;

        const newOtp = otp.split('');
        newOtp[index] = value;
        const otpString = newOtp.join('');
        setOtp(otpString);
        setOtpError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            setOtp(pastedData.padEnd(6, ''));
            // Focus last filled input
            const lastIndex = Math.min(pastedData.length, 5);
            inputRefs.current[lastIndex]?.focus();
        }
    };

    const handleSubmit = () => {
        const validation = validateOTP(otp);
        if (!validation.valid) {
            setOtpError(validation.error);
            return;
        }
        onSubmit();
    };

    const handleResend = () => {
        setResendCooldown(60);
        setCanResend(false);
        onResendOTP();
    };

    const isOtpComplete = otp.length === 6;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Xác Thực OTP
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Nhập mã xác thực 6 số đã được gửi đến email của bạn
            </Typography>

            {/* OTP Input Boxes */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'center',
                    mb: 3,
                }}
            >
                {[0, 1, 2, 3, 4, 5].map((index) => (
                    <TextField
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={otp[index] || ''}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        inputProps={{
                            maxLength: 1,
                            style: {
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                padding: '12px',
                            },
                        }}
                        sx={{
                            width: '50px',
                            '& input': {
                                padding: '12px 0',
                            },
                        }}
                        disabled={loading}
                        error={!!otpError}
                    />
                ))}
            </Box>

            {otpError && (
                <Typography variant="body2" color="error" align="center" sx={{ mb: 2 }}>
                    {otpError}
                </Typography>
            )}

            {/* Countdown Timer */}
            {otpExpiresAt && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Mã OTP hết hạn sau:
                    </Typography>
                    <CountdownTimer expiresAt={otpExpiresAt} />
                </Box>
            )}

            <ErrorMessage error={error} />

            {loading && <LoadingSpinner message="Đang xác thực..." />}

            {!loading && (
                <>
                    {/* Confirmation Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Tôi xác nhận toàn bộ các thông tin đăng ký là chính xác
                            </Typography>
                        }
                        sx={{ mb: 2, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0 } }}
                    />

                    {/* Submit Button */}
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={!isOtpComplete || !confirmed}
                        sx={{ mb: 2 }}
                    >
                        Hoàn Tất Đăng Ký
                    </Button>

                    {/* Resend OTP Button */}
                    <Button
                        fullWidth
                        variant="text"
                        onClick={handleResend}
                        disabled={!canResend}
                    >
                        {canResend
                            ? 'Gửi lại mã OTP'
                            : `Gửi lại sau ${resendCooldown}s`}
                    </Button>
                </>
            )}
        </Box>
    );
};

export default StepOTPVerification;
